import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { Ballast } from "../../../../../http/border/types/ballasts";
import { useBallastsApi } from "../../../../../http/border/useBallasts";
import { AdminItem } from "../../../../../http/types/items";
import { PaginationQuery } from "../../../../../types";
import { itemLabel } from "../../../../../utils";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import BallastForm from "./BallastForm";
import CreateBallastModalDialogButton from "./CreateBallastModalDialogButton";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const Ballasts: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "ballasts" });
  const aborter = useRef(new AbortController());
  const ballastsApi = useBallastsApi();
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "ballasts");

  const isActiveTab = active ?? true;

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await ballastsApi.updateAdminBallastVisibility(id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (id: string, newOrder: number) => {
    try {
      await ballastsApi.updateAdminBallastOrder(id, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [itemToDelete, setItemToDelete] = useState<Ballast>();
  const [itemToEdit, setItemToEdit] = useState<Ballast>();
  const { accessesAction, accessesDialog } = useEntityAccesses<AdminItem>("border.ballast");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = ballastsApi.useAdminBallasts(query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.ballasts.plural);
    setActions(
      <>
        <SearchBar placeholder={i18n.translation.ballasts.search} value={searchTerm} onChange={handleSearchChange} />
        <CreateBallastModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<Ballast>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.items.singular}</th>
            <th align="left">{i18n.translation.ballastTypes.singular}</th>
            <th align="left">{i18n.translation.common.description}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>{itemLabel(item.item)}</td>
            <td>{item.ballastType?.name}</td>
            <td>{item.item.description}</td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.item.id, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.item.id, newOrder)} />
            </td>
          </>
        )}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          accessesAction(item.item),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}

      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.ballasts.edit}>
          <BallastForm ballast={itemToEdit} onClose={() => setItemToEdit(undefined)} />
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.ballasts.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                ballastsApi
                  .deleteAdminBallast(itemToDelete)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.ballasts.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default Ballasts;
